import React, { useMemo, useState } from 'react';
import { Button, Space, Table, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import RoleFilterArea, { DEFAULT_FILTERS, type FilterOptions } from './components/RoleFilterArea';
import RoleDrawer from './components/RoleDrawer';
import RoleAccountsDrawer from './components/RoleAccountsDrawer';
import {
  QUERY_ROLE_BY_PAGE,
  type QueryRoleByPageResponse,
  type QueryRoleByPageVariables,
  type RoleType,
} from './graphql/queries/queryRoleByPage';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

const RolePage: React.FC = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const variables: QueryRoleByPageVariables = useMemo(
    () => ({
      condition: {
        id: filters.id.trim() ? Number(filters.id.trim()) : undefined,
        name: filters.name.trim() || undefined,
        description: filters.description.trim() || undefined,
      },
      pageRequest: {
        page: currentPage,
        size: pageSize,
        sortDirection: 'DESC',
      },
    }),
    [filters, currentPage, pageSize]
  );

  const { data, loading, refetch } = useQuery<QueryRoleByPageResponse, QueryRoleByPageVariables>(QUERY_ROLE_BY_PAGE, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  const pageData = data?.queryRoleByPage;

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [editRecord, setEditRecord] = useState<RoleType | null>(null);
  const [accountsDrawerRecord, setAccountsDrawerRecord] = useState<RoleType | null>(null);

  const columns: TableColumnsType<RoleType> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 96,
        render: (_: unknown, record: RoleType) => (
          <Button
            type="link"
            className="px-0"
            onClick={() => {
              setEditRecord(record);
              setDrawerMode('edit');
              setDrawerVisible(true);
            }}
          >
            {record.id ?? '-'}
          </Button>
        ),
      },
      {
        title: t('central.platformPermission.role.table.name'),
        dataIndex: 'name',
        key: 'name',
        width: 180,
        render: (v) => v || '-',
      },
      {
        title: t('central.platformPermission.role.table.description'),
        dataIndex: 'description',
        key: 'description',
        width: 280,
        ellipsis: { showTitle: false },
        render: (v: string | null) => (
          <Typography.Text style={{ display: 'block' }} ellipsis={{ tooltip: v ?? '-' }}>
            {v ?? '-'}
          </Typography.Text>
        ),
      },
      {
        title: t('components.button.action'),
        key: 'operation',
        width: 120,
        render: (_: unknown, record: RoleType) => (
          <Space size={12}>
            <HasResource resourceCodes={[ResourceCode.menu.ROLE_QUERY]}>
              <Button
                type="link"
                className="px-0"
                onClick={() => setAccountsDrawerRecord(record)}
              >
                {t('central.platformPermission.role.viewAccountsButton')}
              </Button>
            </HasResource>
          </Space>
        ),
      },
    ],
    [t]
  );

  const handleRefresh = async () => {
    await refetch(variables);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <RoleFilterArea
        filters={filters}
        setFilters={setFilters}
      />

      <div className="flex justify-between items-center gap-2 mb-3 pl-0">
          <HasResource resourceCodes={[ResourceCode.role.ADD]}>
              <Button
                  type="primary"
                  className="w-auto min-w-24"
                  onClick={() => {
                      setEditRecord(null);
                      setDrawerMode('create');
                      setDrawerVisible(true);
                  }}
              >
                  <span className="font-bold">{t('central.platformPermission.role.addButton')}</span>
              </Button>
          </HasResource>
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            onClick={() => {
              setCurrentPage(1);
            }}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button
            onClick={() => {
              setFilters(DEFAULT_FILTERS);
              setCurrentPage(1);
            }}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <Table<RoleType>
        columns={columns}
        dataSource={pageData?.content ?? []}
        loading={loading}
        rowKey="id"
        pagination={false}
        size="middle"
        className="mb-4"
      />

      <TablePaginationBar
        total={pageData?.totalElements ?? 0}
        current={currentPage}
        pageSize={pageSize}
        onChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
          refetch({
            ...variables,
            pageRequest: { ...variables.pageRequest, page, size },
          });
        }}
        showQuickJumper={false}
      />

      <RoleDrawer
        visible={drawerVisible}
        mode={drawerMode}
        onClose={() => setDrawerVisible(false)}
        onSuccess={handleRefresh}
        editRecord={drawerMode === 'edit' ? editRecord : null}
      />

      <RoleAccountsDrawer
        visible={accountsDrawerRecord != null}
        roleId={accountsDrawerRecord?.id ?? ''}
        roleName={accountsDrawerRecord?.name ?? ''}
        onClose={() => setAccountsDrawerRecord(null)}
      />
    </div>
  );
};

export default RolePage;
